import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Upload, Download, Sparkles, RefreshCw, Layers, AlertTriangle, ChevronsLeftRight, Plus, ImageIcon, Trash2, CheckCircle2, Clock, XCircle } from "lucide-react";
import Upscaler from "upscaler";
import x2 from "@upscalerjs/esrgan-slim/2x";
import x4 from "@upscalerjs/esrgan-slim/4x";

const isMobile = () => false
// const isMobile = () => /iPhone|iPad|Android/i.test(navigator.userAgent);
const isMobileLowMemory = () => false;
// const isMobileLowMemory = () => isMobile() && (navigator.deviceMemory ?? 2) < 4;

// Safeguard: even on desktop, cap the input resolution before running it through
// the model. Very large photos (e.g. 4000x3000+) can take an extremely long time
// and hog GPU/CPU memory in-browser, which presents as the tab "hanging".
const MAX_DESKTOP_DIMENSION = 1024;

const hasWebGL = () => {
    try {
        const canvas = document.createElement("canvas");
        return !!(canvas.getContext("webgl2") || canvas.getContext("webgl"));
    } catch {
        return false;
    }
};

const base64ToBlobUrl = (base64) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d").drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
                canvas.width = 0;
                canvas.height = 0;
                if (!blob) return reject(new Error("Failed to encode the upscaled result to an image blob."));
                resolve(URL.createObjectURL(blob));
            }, "image/png");
        };
        // NOTE: onerror previously did `img.onerror = reject`, which rejects
        // with the raw DOM Event object instead of an Error. That's what
        // showed up in the console as "Uncaught (in promise) [object Event]".
        img.onerror = () => reject(new Error("Failed to decode the upscaled image result."));
        img.src = base64;
    });
};

// Generic resize helper. Only shrinks images larger than maxPx; never upscales.
const resizeToMax = (fileOrBlobUrl, maxPx) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const isFile = fileOrBlobUrl instanceof Blob;
        const url = isFile ? URL.createObjectURL(fileOrBlobUrl) : fileOrBlobUrl;

        img.onload = () => {
            const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);

            if (scale === 1) {
                // No resize needed
                resolve(url);
                return;
            }

            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            canvas.toBlob((blob) => {
                canvas.width = 0;
                canvas.height = 0;
                if (isFile) URL.revokeObjectURL(url);
                if (!blob) return reject(new Error("Failed to encode resized image to a blob."));
                resolve(URL.createObjectURL(blob));
            }, "image/png");
        };

        // Fix: reject with a real Error (not the raw Event) so it carries a
        // readable message and doesn't crash as an unhandled rejection with
        // "[object Event]". Also revoke the blob URL we created on failure,
        // since nothing downstream will do it for us in the error path.
        img.onerror = () => {
            if (isFile) URL.revokeObjectURL(url);
            reject(new Error(`Couldn't load "${isFile ? fileOrBlobUrl.name : "image"}". The file may be corrupted or in an unsupported format.`));
        };
        img.src = url;
    });
};

const createThumbUrl = (previewUrl) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const MAX = 300;
            const scale = Math.min(1, MAX / Math.max(img.width, img.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                canvas.width = 0;
                canvas.height = 0;
                resolve(blob ? URL.createObjectURL(blob) : "");
            }, "image/jpeg", 0.7);
        };
        img.onerror = () => resolve("");
        img.src = previewUrl;
    });
};

const createImageItem = (file, previewUrl) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    file,
    name: file.name,
    previewUrl,
    originalThumbUrl: "",
    upscaledUrl: "",
    progress: 0,
    status: "ready",
    error: "",
});

const MODELS_BY_SCALE = { 2: x2, 4: x4 };

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    animate: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
    },
    exit: { opacity: 0, y: -12 },
};

const staggerContainer = {
    initial: {},
    animate: {
        transition: { staggerChildren: 0.08, delayChildren: 0.05 },
    },
};

const scaleIn = {
    initial: { opacity: 0, scale: 0.92 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

const LoadingSpinner = React.memo(({ size = 40, className = "", icon: Icon = RefreshCw }) => (
    <span className={`upscaler-spin ${className}`} aria-hidden="true">
        <Icon size={size} className="text-[#D7C7A1]" />
    </span>
));

const STATUS_META = {
    ready: { label: "Ready", icon: Clock, className: "bg-white/10 text-white/60 border-white/10" },
    processing: { label: "Processing", icon: RefreshCw, className: "bg-[#2F8F6F]/20 text-[#D7C7A1] border-[#2F8F6F]/30" },
    done: { label: "Done", icon: CheckCircle2, className: "bg-[#2F8F6F]/25 text-[#D7C7A1] border-[#2F8F6F]/40" },
    error: { label: "Failed", icon: XCircle, className: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export default function ImageUpscaler() {
    const [images, setImages] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [globalError, setGlobalError] = useState("");
    const [mobileWarning, setMobileWarning] = useState(false);
    const [sliderPos, setSliderPos] = useState(50);
    const [scaleFactor, setScaleFactor] = useState(4);

    const fileInputRef = useRef(null);
    const addMoreInputRef = useRef(null);
    const upscalerRef = useRef(null);
    const loadedScaleRef = useRef(null);

    // Always-current mirror of `images`, used so cleanup-on-unmount can see the
    // latest state without needing `images` in that effect's dependency array
    // (which previously caused the cleanup to run on every progress update).
    const imagesRef = useRef(images);
    useEffect(() => {
        imagesRef.current = images;
    }, [images]);

    const activeImage = images.find((img) => img.id === activeId) ?? images[0] ?? null;
    const pendingCount = images.filter((img) => img.status !== "done").length;
    const doneCount = images.filter((img) => img.status === "done").length;

    useEffect(() => {
        if (isMobileLowMemory()) setMobileWarning(true);
        if (!hasWebGL()) {
            setGlobalError("WebGL is not supported in this browser. Upscaling requires GPU acceleration.");
            return;
        }
        if (!upscalerRef.current) {
            try {
                upscalerRef.current = new Upscaler({ model: MODELS_BY_SCALE[scaleFactor] });
                loadedScaleRef.current = scaleFactor;
            } catch (err) {
                console.error("Failed to initialize upscaler:", err);
                setGlobalError("Failed to load AI model. Try refreshing the page.");
            }
        }

        // A WebGL context loss usually happens right before the browser would
        // otherwise kill the whole tab for running out of GPU memory. Catching
        // it lets us show a clear message instead of the tab just crashing.
        const handleContextLost = (e) => {
            e.preventDefault();
            console.error("WebGL context lost — likely ran out of GPU memory.");
            setGlobalError(
                "The GPU ran out of memory and the graphics context was lost. Try smaller images, fewer images at once, or restart the browser."
            );
            setIsProcessing(false);
        };
        window.addEventListener("webglcontextlost", handleContextLost, true);

        return () => {
            window.removeEventListener("webglcontextlost", handleContextLost, true);
        };
    }, []);

    // Revoke all blob URLs ONLY when the component actually unmounts.
    // Empty dependency array is intentional and required — this must not
    // re-run (and therefore re-fire its cleanup) every time `images` changes.
    useEffect(() => {
        return () => {
            imagesRef.current.forEach((img) => {
                if (img.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(img.previewUrl);
                if (img.originalThumbUrl?.startsWith("blob:")) URL.revokeObjectURL(img.originalThumbUrl);
                if (img.upscaledUrl?.startsWith("blob:")) URL.revokeObjectURL(img.upscaledUrl);
            });
        };
    }, []);

    const updateImage = (id, updates) => {
        setImages((prev) => prev.map((img) => (img.id === id ? { ...img, ...updates } : img)));
    };

    const revokeImageUrls = (img) => {
        if (img.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(img.previewUrl);
        if (img.originalThumbUrl?.startsWith("blob:")) URL.revokeObjectURL(img.originalThumbUrl);
        if (img.upscaledUrl?.startsWith("blob:")) URL.revokeObjectURL(img.upscaledUrl);
    };

    const addFiles = async (fileList) => {
        const files = Array.from(fileList).filter((file) => file.type.startsWith("image/"));
        if (!files.length) return;

        setGlobalError("");

        // Fix: use allSettled instead of Promise.all. Previously, if ANY single
        // file failed to decode (corrupt file, unsupported format, etc.), the
        // whole Promise.all rejected. Since addFiles is called via
        // `await addFiles(...)` with no try/catch in handleFileChange/handleDrop,
        // that rejection had nowhere to go and surfaced as an unhandled
        // "Uncaught (in promise)" error — killing the entire batch, including
        // files that loaded fine.
        const results = await Promise.allSettled(
            files.map(async (file) => {
                const previewUrl = isMobile()
                    ? await resizeToMax(file, 512)
                    : await resizeToMax(file, MAX_DESKTOP_DIMENSION);
                return createImageItem(file, previewUrl);
            })
        );

        const newItems = [];
        const failedNames = [];

        results.forEach((result, i) => {
            if (result.status === "fulfilled") {
                newItems.push(result.value);
            } else {
                console.error("Failed to add file:", files[i]?.name, result.reason);
                failedNames.push(files[i]?.name ?? "unknown file");
            }
        });

        if (newItems.length) {
            setImages((prev) => [...prev, ...newItems]);
            setActiveId((prev) => prev ?? newItems[0].id);
        }

        if (failedNames.length) {
            setGlobalError(
                `Couldn't load ${failedNames.length === 1 ? "this file" : "these files"}: ${failedNames.join(", ")}. Try a different image.`
            );
        }
    };

    const handleFileChange = async (e) => {
        try {
            await addFiles(e.target.files);
        } catch (err) {
            console.error("Unexpected error adding files:", err);
            setGlobalError("Something went wrong while adding your files. Please try again.");
        } finally {
            e.target.value = "";
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        try {
            await addFiles(e.dataTransfer.files);
        } catch (err) {
            console.error("Unexpected error adding dropped files:", err);
            setGlobalError("Something went wrong while adding your files. Please try again.");
        }
    };

    // Fully tears down the current model (freeing its GPU/WebGL memory) and
    // creates a fresh instance for the given scale. Without this, memory used
    // by the model's internal tensors can accumulate across images in a queue
    // until the browser kills the tab for running out of memory ("Aw, Snap!").
    const resetUpscalerModel = (scale = scaleFactor) => {
        try {
            upscalerRef.current?.dispose?.();
        } catch (err) {
            console.warn("Error disposing upscaler model:", err);
        }
        upscalerRef.current = new Upscaler({ model: MODELS_BY_SCALE[scale] });
        loadedScaleRef.current = scale;
    };

    const upscaleSingle = async (item) => {
        // Make sure the loaded model matches the currently selected scale
        // (user may have switched between 2x/4x since the model was created).
        if (!upscalerRef.current || loadedScaleRef.current !== scaleFactor) {
            resetUpscalerModel(scaleFactor);
        }

        // Smaller patches = smaller intermediate WebGL tensors = lower peak
        // memory. 64px patches at 4x scale can spike memory enough to crash
        // the tab's renderer process on machines with limited GPU memory
        // (integrated graphics, memory-constrained Macs, etc).
        const patchSize = isMobileLowMemory() ? 16 : 32;
        const padding = isMobileLowMemory() ? 2 : 4;

        updateImage(item.id, { status: "processing", progress: 5, error: "" });

        const thumbUrl = await createThumbUrl(item.previewUrl);

        // Throttle progress updates so we don't trigger a React re-render of the
        // whole images array on every single patch (which is what was freezing
        // the tab on larger images with lots of patches).
        let lastProgressUpdate = 0;
        const PROGRESS_THROTTLE_MS = 150;

        try {
            const result = await upscalerRef.current.upscale(item.previewUrl, {
                output: "base64",
                patchSize,
                padding,
                progress: (amount) => {
                    const now = Date.now();
                    const pct = Math.round(amount * 100);
                    // Always let the final 100% through immediately.
                    if (pct >= 100 || now - lastProgressUpdate >= PROGRESS_THROTTLE_MS) {
                        lastProgressUpdate = now;
                        updateImage(item.id, { progress: pct });
                    }
                },
            });

            if (item.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(item.previewUrl);

            const blobUrl = await base64ToBlobUrl(result);
            updateImage(item.id, {
                previewUrl: "",
                originalThumbUrl: thumbUrl,
                upscaledUrl: blobUrl,
                progress: 100,
                status: "done",
                scale: scaleFactor,
            });
        } catch (err) {
            if (thumbUrl?.startsWith("blob:")) URL.revokeObjectURL(thumbUrl);
            console.error("Upscaling error:", err);
            const message = /out of memory|vram|webgl/i.test(err.message ?? "")
                ? "GPU memory exceeded. Try a smaller image or use a desktop browser."
                : "Upscaling failed. Try a smaller image or refresh and try again.";
            updateImage(item.id, { status: "error", error: message, progress: 0 });
        }
    };

    const handleUpscale = async () => {
        if (!images.length) return;

        setIsProcessing(true);
        setGlobalError("");

        const queue = images.filter((img) => img.status !== "done");
        for (let i = 0; i < queue.length; i++) {
            const item = queue[i];
            setActiveId(item.id);
            await upscaleSingle(item);

            // Release the model's accumulated GPU memory before starting the
            // next image. This is what prevents multi-image batches from
            // slowly building up memory until the tab crashes.
            if (i < queue.length - 1) {
                resetUpscalerModel();
                // Give the browser a moment to actually reclaim the freed
                // WebGL memory before the next heavy allocation.
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
        }

        setIsProcessing(false);
    };

    const downloadImage = async (item) => {
        if (!item?.upscaledUrl) return;
        try {
            const response = await fetch(item.upscaledUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const baseName = item.name.replace(/\.[^.]+$/, "") || "image";
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${baseName}-upscaled-${item.scale ?? scaleFactor}x.png`;
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 150);
        } catch {
            window.open(item.upscaledUrl, "_blank");
        }
    };

    const handleDownloadAll = async () => {
        const doneImages = images.filter((img) => img.upscaledUrl);
        for (const item of doneImages) {
            await downloadImage(item);
        }
    };

    const removeImage = (id) => {
        setImages((prev) => {
            const target = prev.find((img) => img.id === id);
            if (target) revokeImageUrls(target);
            const next = prev.filter((img) => img.id !== id);
            setActiveId((current) => {
                if (current !== id) return current;
                return next[0]?.id ?? null;
            });
            return next;
        });
    };

    const resetTool = () => {
        images.forEach(revokeImageUrls);
        setImages([]);
        setActiveId(null);
        setGlobalError("");
        setSliderPos(50);
    };

    const showUploadZone = images.length === 0;
    const activeProgress = activeImage?.status === "processing" ? activeImage.progress : 0;

    return (
        <div className="min-h-screen bg-[#0F0F0F] text-white flex flex-col font-sans pb-12 overflow-hidden">
            <div className="relative bg-gradient-to-br from-[#0B3D2E] via-[#145A43] to-[#0B3D2E] border-b border-[#2F8F6F]/20 px-6 py-12 md:py-16 shadow-lg w-full overflow-hidden">
                <motion.div
                    animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.45, 0.25] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-20 -left-20 w-64 h-64 bg-[#2F8F6F]/20 rounded-full blur-3xl pointer-events-none"
                />
                <motion.div
                    animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                    className="absolute -bottom-16 -right-16 w-80 h-80 bg-[#D7C7A1]/10 rounded-full blur-3xl pointer-events-none"
                />

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="relative max-w-2xl mx-auto flex flex-col justify-center items-center text-center space-y-3"
                >
                    <motion.div
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
                        className="p-3 rounded-2xl bg-[#2F8F6F]/20 border border-[#2F8F6F]/30 mb-1"
                    >
                        <Sparkles size={22} className="text-[#D7C7A1]" />
                    </motion.div>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-[#D7C7A1]">
                        AI Image Upscaler
                    </h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.35, duration: 0.5 }}
                        className="text-[#D7C7A1] text-xs sm:text-sm max-w-md font-medium leading-relaxed"
                    >
                        Enhance quality and boost resolution instantly using local machine learning
                    </motion.p>
                </motion.div>
            </div>

            <div className="w-full max-w-5xl mx-auto p-6 md:p-8">

                <AnimatePresence mode="popLayout">
                    {mobileWarning && !globalError && (
                        <motion.div
                            key="mobile-warning"
                            initial={{ opacity: 0, y: -8, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -8, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mb-4 p-4 rounded-xl border border-yellow-500/30 bg-yellow-900/20 text-yellow-200 text-xs font-bold flex items-start gap-2 overflow-hidden"
                        >
                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-yellow-400" />
                            <span>Mobile detected. Images are pre-scaled to 512px max before processing to prevent crashes.</span>
                        </motion.div>
                    )}

                    {globalError && (
                        <motion.div
                            key="global-error"
                            initial={{ opacity: 0, y: -8, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -8, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mb-4 p-4 rounded-xl border border-red-500/30 bg-red-900/20 text-red-200 text-xs font-bold flex items-start gap-2 overflow-hidden"
                        >
                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-400" />
                            <span>{globalError}</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence mode="wait">
                    {showUploadZone && (
                        <motion.div
                            key="upload-zone"
                            {...scaleIn}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                            whileHover={{ scale: 1.01, borderColor: "rgba(47, 143, 111, 0.6)" }}
                            whileTap={{ scale: 0.99 }}
                            className="group border-2 border-dashed border-[#2F8F6F]/30 rounded-2xl p-10 md:p-16 flex flex-col items-center justify-center gap-4 cursor-pointer bg-[#145A43]/10 hover:bg-[#145A43]/20 transition-colors duration-300"
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                multiple
                                className="hidden"
                            />
                            <motion.div
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                                className="p-4 rounded-full bg-[#145A43] text-[#D7C7A1] shadow-md shadow-[#0B3D2E]/50"
                            >
                                <motion.div whileHover={{ scale: 1.1, rotate: 5 }} transition={{ type: "spring", stiffness: 400 }}>
                                    <Upload size={32} />
                                </motion.div>
                            </motion.div>
                            <div className="text-center">
                                <p className="text-sm font-bold text-white/80">
                                    Drag & drop your images here, or <span className="text-[#D7C7A1]">browse</span>
                                </p>
                                <p className="text-xs text-white/40 mt-1">
                                    {isMobileLowMemory() ? "PNG, JPG, WEBP — max 2MB on mobile" : "Supports PNG, JPG, WEBP — select multiple files"}
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {!showUploadZone && activeImage && (
                        <motion.div
                            key="workspace"
                            variants={staggerContainer}
                            initial="initial"
                            animate="animate"
                            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                        >

                            <motion.div variants={fadeUp} className="lg:col-span-2 flex flex-col gap-4">
                                {images.length > 1 && (
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {images.map((img, index) => (
                                            <motion.button
                                                key={img.id}
                                                type="button"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: index * 0.05, type: "spring", stiffness: 400, damping: 25 }}
                                                whileHover={img.status === "processing" ? undefined : { scale: 1.08, y: -2 }}
                                                whileTap={img.status === "processing" ? undefined : { scale: 0.95 }}
                                                onClick={() => setActiveId(img.id)}
                                                className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${img.id === activeImage.id
                                                    ? "border-[#2F8F6F] ring-2 ring-[#2F8F6F]/40"
                                                    : "border-[#2F8F6F]/20 opacity-70 hover:opacity-100"
                                                    }`}
                                            >
                                                <img
                                                    src={img.upscaledUrl || img.previewUrl}
                                                    alt={img.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                {img.status === "done" && (
                                                    <motion.span
                                                        initial={{ y: 8, opacity: 0 }}
                                                        animate={{ y: 0, opacity: 1 }}
                                                        className="absolute bottom-0 inset-x-0 bg-[#2F8F6F]/90 text-[8px] font-bold text-center py-0.5"
                                                    >
                                                        {img.scale ?? scaleFactor}x
                                                    </motion.span>
                                                )}
                                                {img.status === "processing" && (
                                                    <span className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                        <LoadingSpinner size={14} />
                                                    </span>
                                                )}
                                            </motion.button>
                                        ))}
                                    </div>
                                )}

                                <motion.div
                                    variants={fadeUp}
                                    className="relative min-h-[350px] max-h-[500px] bg-[#0B3D2E]/40 border border-[#2F8F6F]/20 rounded-2xl overflow-hidden flex items-center justify-center p-4"
                                >
                                    <AnimatePresence mode="wait">
                                        {!activeImage.upscaledUrl ? (
                                            <motion.img
                                                key={`preview-${activeImage.id}`}
                                                src={activeImage.previewUrl}
                                                alt="Source preview"
                                                initial={{ opacity: 0, scale: 0.96 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.98 }}
                                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                                className="max-h-[440px] object-contain rounded-lg shadow-lg"
                                            />
                                        ) : (
                                            <motion.div
                                                key={`compare-${activeImage.id}`}
                                                initial={{ opacity: 0, scale: 0.98 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                                className="relative w-full h-full min-h-[450px] overflow-hidden rounded-xl border border-[#2F8F6F]/30 bg-black"
                                            >
                                                <img
                                                    src={activeImage.upscaledUrl}
                                                    alt="Upscaled"
                                                    className="absolute inset-0 w-full h-full object-contain"
                                                    draggable={false}
                                                />

                                                <div
                                                    className="absolute inset-0 overflow-hidden"
                                                    style={{
                                                        clipPath: `inset(0 ${100 - sliderPos}% 0 0)`
                                                    }}
                                                >
                                                    <img
                                                        src={activeImage.originalThumbUrl}
                                                        alt="Original"
                                                        className="w-full h-full object-contain"
                                                        draggable={false}
                                                    />
                                                </div>

                                                <div
                                                    className="absolute top-0 bottom-0 z-20"
                                                    style={{ left: `${sliderPos}%` }}
                                                >
                                                    <div className="relative h-full">
                                                        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-[#2F8F6F] shadow-lg" />

                                                        <motion.div
                                                            animate={{ scale: [1, 1.08, 1] }}
                                                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                                            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#2F8F6F] shadow-xl flex items-center justify-center border-2 border-[#2F8F6F] text-white"
                                                        >
                                                            <ChevronsLeftRight size={18} />
                                                        </motion.div>
                                                    </div>
                                                </div>

                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={sliderPos}
                                                    onChange={(e) => setSliderPos(Number(e.target.value))}
                                                    className="absolute inset-0 z-30 opacity-0 cursor-ew-resize"
                                                />

                                                <motion.span
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.2 }}
                                                    className="absolute top-3 left-3 z-20 bg-black/70 text-white text-xs px-3 py-1 rounded-full border border-[#2F8F6F]"
                                                >
                                                    Original
                                                </motion.span>

                                                <motion.span
                                                    initial={{ opacity: 0, x: 10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.2 }}
                                                    className="absolute top-3 right-3 z-20 bg-[#2F8F6F]/90 text-white text-xs px-3 py-1 rounded-full"
                                                >
                                                    Upscaled {activeImage.scale ?? scaleFactor}x
                                                </motion.span>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <AnimatePresence>
                                        {isProcessing && activeImage.status === "processing" && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.25 }}
                                                className="absolute inset-0 bg-[#0F0F0F]/80 backdrop-blur-md flex flex-col items-center justify-center p-6"
                                            >
                                                <LoadingSpinner size={40} className="mb-4" />
                                                <motion.p
                                                    animate={{ opacity: [0.6, 1, 0.6] }}
                                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                                    className="text-sm font-bold tracking-wider text-white"
                                                >
                                                    AI IS ENHANCING PIXELS...
                                                </motion.p>
                                                <div className="w-48 bg-white/10 h-1.5 rounded-full mt-3 overflow-hidden border border-white/5">
                                                    <motion.div
                                                        className="bg-gradient-to-r from-[#2F8F6F] to-[#D7C7A1] h-full"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${activeProgress}%` }}
                                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                                    />
                                                </div>
                                                <motion.p
                                                    key={activeProgress}
                                                    initial={{ opacity: 0, y: 4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-[10px] text-white/50 mt-1"
                                                >
                                                    {activeProgress}% Complete
                                                </motion.p>
                                                {images.length > 1 && (
                                                    <p className="text-[10px] text-white/40 mt-2">
                                                        Processing {doneCount + 1} of {images.length}
                                                    </p>
                                                )}
                                                {isMobile() && (
                                                    <p className="text-[10px] text-yellow-400/70 mt-2 text-center max-w-[180px]">
                                                        Processing on mobile — keep this tab open
                                                    </p>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>

                                <AnimatePresence>
                                    {activeImage.error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -8, height: 0 }}
                                            animate={{ opacity: 1, y: 0, height: "auto" }}
                                            exit={{ opacity: 0, y: -8, height: 0 }}
                                            className="p-3 rounded-xl border border-red-500/30 bg-red-900/20 text-red-200 text-xs font-bold flex items-start gap-2 overflow-hidden"
                                        >
                                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-400" />
                                            <span>{activeImage.error}</span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>

                            <motion.div
                                variants={fadeUp}
                                className="bg-[#145A43]/30 border border-[#2F8F6F]/20 rounded-2xl p-5 flex flex-col justify-between gap-6"
                            >
                                <div className="space-y-5">
                                    <div>
                                        <label className="text-xs font-black tracking-wider text-[#D7C7A1] uppercase block mb-2">
                                            Upscale Factor
                                        </label>
                                        <div className="relative grid grid-cols-2 gap-2 bg-[#0F0F0F]/60 p-1.5 rounded-xl border border-[#2F8F6F]/10">
                                            <motion.div
                                                layoutId="scale-indicator"
                                                className="absolute top-1.5 bottom-1.5 rounded-lg bg-[#2F8F6F] shadow"
                                                style={{
                                                    left: scaleFactor === 2 ? "6px" : "calc(50% + 2px)",
                                                    width: "calc(50% - 8px)",
                                                }}
                                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                            />
                                            <motion.button
                                                type="button"
                                                onClick={() => setScaleFactor(2)}
                                                disabled={isProcessing}
                                                whileTap={{ scale: 0.97 }}
                                                className={`relative z-10 py-2 text-xs font-black rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${scaleFactor === 2
                                                    ? "text-white"
                                                    : "text-white/50 hover:text-white/80"
                                                    }`}
                                            >
                                                200% (2x)
                                            </motion.button>
                                            <motion.button
                                                type="button"
                                                onClick={() => setScaleFactor(4)}
                                                disabled={isProcessing}
                                                whileTap={{ scale: 0.97 }}
                                                className={`relative z-10 py-2 text-xs font-black rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${scaleFactor === 4
                                                    ? "text-white"
                                                    : "text-white/50 hover:text-white/80"
                                                    }`}
                                            >
                                                400% (4x)
                                            </motion.button>
                                        </div>
                                        <p className="text-[10px] text-white/30 mt-1.5">
                                            Only applies to images not yet upscaled.
                                        </p>
                                    </div>

                                    {images.length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.15 }}
                                            className="bg-[#0B3D2E]/50 border border-[#2F8F6F]/20 rounded-xl overflow-hidden"
                                        >
                                            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-[#2F8F6F]/15 bg-[#0F0F0F]/20">
                                                <span className="text-[11px] font-bold text-white/80 flex items-center gap-1.5">
                                                    <ImageIcon size={13} className="text-[#D7C7A1]" />
                                                    Uploaded
                                                    <span className="text-[#2F8F6F]">({images.length})</span>
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    {doneCount > 0 && (
                                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-[#2F8F6F]/20 text-[#D7C7A1] border border-[#2F8F6F]/25">
                                                            {doneCount} done
                                                        </span>
                                                    )}
                                                    {pendingCount > 0 && (
                                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-white/5 text-white/50 border border-white/10">
                                                            {pendingCount} pending
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <ul className="max-h-44 overflow-y-auto p-2 space-y-1.5 [scrollbar-width:thin] [scrollbar-color:#2F8F6F40_transparent]">
                                                <AnimatePresence initial={false}>
                                                    {images.map((img, index) => {
                                                        const status = STATUS_META[img.status] ?? STATUS_META.ready;
                                                        const StatusIcon = status.icon;
                                                        const thumbSrc = img.upscaledUrl || img.previewUrl || img.originalThumbUrl;
                                                        const isActive = img.id === activeImage?.id;

                                                        return (
                                                            <motion.li
                                                                key={img.id}
                                                                layout
                                                                initial={{ opacity: 0, y: 8, scale: 0.98 }}
                                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                exit={{ opacity: 0, x: 16, scale: 0.96, transition: { duration: 0.2 } }}
                                                                transition={{ delay: index * 0.04, type: "spring", stiffness: 420, damping: 28 }}
                                                            >
                                                                <motion.div
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() => setActiveId(img.id)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            e.preventDefault();
                                                                            setActiveId(img.id);
                                                                        }
                                                                    }}
                                                                    whileHover={{ backgroundColor: "rgba(47, 143, 111, 0.12)" }}
                                                                    className={`group w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors border cursor-pointer ${isActive
                                                                        ? "bg-[#2F8F6F]/15 border-[#2F8F6F]/40 ring-1 ring-[#2F8F6F]/20"
                                                                        : "bg-[#0F0F0F]/30 border-transparent hover:border-[#2F8F6F]/20"
                                                                        }`}
                                                                >
                                                                    <div className="relative shrink-0 w-9 h-9 rounded-md overflow-hidden border border-[#2F8F6F]/25 bg-[#0F0F0F]/60">
                                                                        {thumbSrc ? (
                                                                            <img
                                                                                src={thumbSrc}
                                                                                alt=""
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        ) : (
                                                                            <div className="w-full h-full flex items-center justify-center">
                                                                                <ImageIcon size={14} className="text-white/30" />
                                                                            </div>
                                                                        )}
                                                                        {img.status === "processing" && (
                                                                            <span className="absolute inset-0 bg-black/55 flex items-center justify-center">
                                                                                <LoadingSpinner size={12} />
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="min-w-0 flex-1">
                                                                        <p className="text-[10px] font-semibold text-white/80 truncate leading-tight">
                                                                            {img.name}
                                                                        </p>
                                                                        <div className="mt-1 flex items-center gap-1.5">
                                                                            <span className={`inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full border ${status.className}`}>
                                                                                <StatusIcon size={8} className={img.status === "processing" ? "opacity-80" : ""} />
                                                                                {img.status === "done" && img.scale ? `${img.scale}x` : status.label}
                                                                            </span>
                                                                            {img.status === "processing" && (
                                                                                <span className="text-[8px] text-white/40 font-medium">
                                                                                    {img.progress}%
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {img.status === "processing" && (
                                                                            <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                                                                                <motion.div
                                                                                    className="h-full bg-gradient-to-r from-[#2F8F6F] to-[#D7C7A1]"
                                                                                    initial={{ width: 0 }}
                                                                                    animate={{ width: `${img.progress}%` }}
                                                                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <motion.button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeImage(img.id);
                                                                        }}
                                                                        disabled={isProcessing}
                                                                        whileHover={{ scale: 1.08, backgroundColor: "rgba(239, 68, 68, 0.15)" }}
                                                                        whileTap={{ scale: 0.92 }}
                                                                        aria-label={`Remove ${img.name}`}
                                                                        className="shrink-0 p-1.5 rounded-md text-red-300/60 hover:text-red-200 border border-transparent hover:border-red-500/25 disabled:opacity-40 disabled:pointer-events-none"
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </motion.button>
                                                                </motion.div>
                                                            </motion.li>
                                                        );
                                                    })}
                                                </AnimatePresence>
                                            </ul>
                                        </motion.div>
                                    )}

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="bg-[#0B3D2E]/40 border border-[#2F8F6F]/10 rounded-xl p-3"
                                    >
                                        <span className="text-[11px] font-bold text-white/70 block mb-1 flex items-center gap-1">
                                            <Layers size={12} className="text-[#D7C7A1]" /> Output Details:
                                        </span>
                                        <ul className="text-[10px] text-white/50 space-y-1 list-disc list-inside">
                                            <li>ESRGAN Slim {scaleFactor}x model</li>
                                            <li>Patch-based GPU rendering</li>
                                            <li>{isMobileLowMemory() ? "Low-memory mode (16px patches)" : "Standard mode (32px patches)"}</li>
                                            <li>{isMobile() ? "Input pre-scaled to 512px max" : `Input capped at ${MAX_DESKTOP_DIMENSION}px max`}</li>
                                            <li>Output format: PNG</li>
                                        </ul>
                                    </motion.div>
                                </div>

                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.25 }}
                                    className="space-y-2 pt-4 border-t border-[#2F8F6F]/10"
                                >
                                    <input
                                        type="file"
                                        ref={addMoreInputRef}
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                    />

                                    <AnimatePresence mode="wait">
                                        {pendingCount > 0 ? (
                                            <motion.button
                                                key="upscale-btn"
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -8 }}
                                                onClick={handleUpscale}
                                                disabled={isProcessing}
                                                whileHover={{ scale: 1.02, boxShadow: "0 8px 24px rgba(47, 143, 111, 0.35)" }}
                                                whileTap={{ scale: 0.98 }}
                                                className="w-full bg-[#2F8F6F] hover:bg-[#2F8F6F]/80 text-white font-extrabold text-xs py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                            >
                                                {isProcessing ? (
                                                    <LoadingSpinner size={16} icon={Sparkles} />
                                                ) : (
                                                    <Sparkles size={16} className="text-[#D7C7A1]" />
                                                )}
                                                {images.length > 1 ? `Upscale ${pendingCount} Image${pendingCount > 1 ? "s" : ""}` : "Upscale Image"}
                                            </motion.button>
                                        ) : (
                                            <motion.div
                                                key="download-btns"
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -8 }}
                                                className="space-y-2"
                                            >
                                                <motion.button
                                                    onClick={() => downloadImage(activeImage)}
                                                    whileHover={{ scale: 1.02, boxShadow: "0 8px 24px rgba(47, 143, 111, 0.35)" }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className="w-full bg-[#2F8F6F] hover:bg-[#2F8F6F]/90 text-white font-extrabold text-xs py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <Download size={16} className="text-[#D7C7A1]" />
                                                    Download HD Image
                                                </motion.button>
                                                {doneCount > 1 && (
                                                    <motion.button
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        onClick={handleDownloadAll}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        className="w-full bg-[#145A43] hover:bg-[#145A43]/80 border border-[#2F8F6F]/30 text-white font-bold text-xs py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                                                    >
                                                        <Download size={14} className="text-[#D7C7A1]" />
                                                        Download All ({doneCount})
                                                    </motion.button>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <motion.button
                                        type="button"
                                        onClick={() => addMoreInputRef.current?.click()}
                                        disabled={isProcessing}
                                        whileHover={{ scale: 1.01, backgroundColor: "rgba(255,255,255,0.1)" }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white font-bold text-xs py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        <Plus size={14} />
                                        Add More Images
                                    </motion.button>

                                    <motion.button
                                        onClick={resetTool}
                                        disabled={isProcessing}
                                        whileHover={{ scale: 1.01, backgroundColor: "rgba(255,255,255,0.1)" }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white font-bold text-xs py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        <RefreshCw size={14} />
                                        Clear / Upload New
                                    </motion.button>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="text-gray-400 flex w-full justify-center text-xs md:text-md text-end bg-[#0F0F0F] pb-10"
            >
                <p>Copyright © 2025 Rizki Makruf. All rights reserved.</p>
            </motion.div>
        </div>
    );
}